import React, { useCallback, useEffect, useState } from "react";
import {
  makeStyles,
  tokens,
  Text,
  Button,
  Spinner,
  Textarea,
  Card,
  CardHeader,
  Badge,
  MessageBar,
  MessageBarBody,
} from "@fluentui/react-components";
import {
  ArrowLeft24Regular,
  ArrowSync24Regular,
  Eye24Regular,
  Send24Regular,
  ArrowDownload24Regular,
  Open24Regular,
} from "@fluentui/react-icons";

import { useProposalApi } from "./useProposalApi";
import type { Proposal } from "./types";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflowY: "auto",
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  content: {
    flex: 1,
    padding: tokens.spacingVerticalL,
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalL,
  },
  sectionCard: {
    maxWidth: "700px",
  },
  sectionBody: {
    padding: tokens.spacingHorizontalM,
    maxHeight: "300px",
    overflowY: "auto",
  },
  pdfFrame: {
    width: "100%",
    height: "600px",
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  regenArea: {
    display: "flex",
    gap: tokens.spacingHorizontalS,
    alignItems: "flex-end",
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
});

interface ProposalPreviewProps {
  proposal: Proposal;
  onBack: () => void;
  onEdit: () => void;
  onSubmitted: (proposal: Proposal) => void;
  onRefresh: (proposal: Proposal) => void;
}

export const ProposalPreview: React.FC<ProposalPreviewProps> = ({
  proposal,
  onBack,
  onEdit,
  onSubmitted,
  onRefresh,
}) => {
  const styles = useStyles();
  const proposalApi = useProposalApi();

  const [generating, setGenerating] = useState(false);
  const [regenning, setRegenning] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [instruction, setInstruction] = useState("");
  const [error, setError] = useState<string | null>(null);

  const transmittalText =
    proposal.composition?.transmittal?.text_snapshot || null;

  // Auto-generate transmittal letter on first visit if not yet generated
  useEffect(() => {
    if (transmittalText || regenning) return;
    let cancelled = false;
    (async () => {
      setRegenning(true);
      try {
        const result = await proposalApi.regenSection(proposal.id, "transmittal");
        if (!cancelled) onRefresh(result.proposal);
      } catch {
        // silent — user can still click Regenerate manually
      } finally {
        if (!cancelled) setRegenning(false);
      }
    })();
    return () => { cancelled = true; };
    // only run once on mount when there's no transmittal
    // eslint-disable-next-line
  }, []);

  const handleGeneratePreview = useCallback(async () => {
    setGenerating(true);
    setError(null);
    setPreviewUrl(null);
    try {
      const result = await proposalApi.preview(proposal.id);
      onRefresh(result.proposal);
      const blobUrl = await proposalApi.previewPdfBlobUrl(proposal.id);
      setPreviewUrl(blobUrl);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Preview failed");
    } finally {
      setGenerating(false);
    }
  }, [proposalApi, proposal.id, onRefresh]);

  const handleRegenTransmittal = useCallback(async () => {
    setRegenning(true);
    setError(null);
    try {
      const result = await proposalApi.regenSection(
        proposal.id,
        "transmittal",
        instruction,
      );
      onRefresh(result.proposal);
      setInstruction("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Regeneration failed");
    } finally {
      setRegenning(false);
    }
  }, [proposalApi, proposal.id, instruction, onRefresh]);

  const handleSubmit = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    try {
      const updated = await proposalApi.submit(proposal.id);
      onSubmitted(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Submit failed");
    } finally {
      setSubmitting(false);
    }
  }, [proposalApi, proposal.id, onSubmitted]);

  const handleDownloadDraft = useCallback(async () => {
    try {
      const url = await proposalApi.download(proposal.id, "draft_pdf");
      window.open(url, "_blank");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Download failed");
    }
  }, [proposalApi, proposal.id]);

  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        <div style={{ display: "flex", alignItems: "center", gap: tokens.spacingHorizontalS }}>
          <Button appearance="subtle" icon={<ArrowLeft24Regular />} onClick={onBack}>
            Back
          </Button>
          <Text size={400} weight="semibold">
            Preview — {proposal.model.proposal_id || proposal.id.slice(0, 8)}
          </Text>
        </div>
        <Button appearance="subtle" onClick={onEdit}>
          Edit Fields
        </Button>
      </div>

      {error && (
        <MessageBar intent="error" style={{ margin: tokens.spacingHorizontalM }}>
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      <div className={styles.content}>
        {/* Transmittal Section */}
        <Card className={styles.sectionCard}>
          <CardHeader
            header={
              <Text weight="semibold">
                Section I — Transmittal Letter{" "}
                <Badge color="informative" appearance="tint">
                  LLM-generated
                </Badge>
              </Text>
            }
          />
          <div className={styles.sectionBody} style={{ maxHeight: "400px" }}>
            {transmittalText ? (
              <Text style={{ whiteSpace: "pre-wrap" }}>{transmittalText}</Text>
            ) : regenning ? (
              <Spinner size="small" label="Generating transmittal letter…" />
            ) : (
              <Text style={{ color: tokens.colorNeutralForeground3, fontStyle: "italic" }}>
                Generation failed. Click "Regenerate" below to try again.
              </Text>
            )}
            <div
              className={styles.regenArea}
              style={{ marginTop: tokens.spacingVerticalM }}
            >
              <Textarea
                style={{ flex: 1 }}
                value={instruction}
                onChange={(_, d) => setInstruction(d.value)}
                placeholder="Optional: guide the tone or content (max 500 chars)"
                rows={2}
                maxLength={500}
              />
              <Button
                appearance="outline"
                icon={<ArrowSync24Regular />}
                onClick={handleRegenTransmittal}
                disabled={regenning}
              >
                {regenning ? <Spinner size="tiny" /> : transmittalText ? "Regenerate" : "Generate"}
              </Button>
            </div>
          </div>
        </Card>

        {/* Static sections summary */}
        <Card className={styles.sectionCard}>
          <CardHeader header={<Text weight="semibold">Other Sections</Text>} />
          <div className={styles.sectionBody} style={{ display: "flex", flexDirection: "column", gap: tokens.spacingVerticalXS }}>
            <Text>II. Open-Source Vendor — <Badge appearance="tint">Template</Badge></Text>
            <Text>III. Solution Overview — <Badge appearance="tint">From line items ({proposal.model.line_items.length} items)</Badge></Text>
            <Text>IV. Service & Warranty — <Badge appearance="tint">Template ({proposal.model.term_months} months)</Badge></Text>
            <Text>V. Services Total — <Badge appearance="tint">Template + pricing</Badge></Text>
            <Text>VI. Regional Success — <Badge appearance="tint">Hardcoded references</Badge></Text>
            <Text>VII. Compliance — <Badge appearance="tint">Template</Badge></Text>
            <Text>VIII. Contact Info — <Badge appearance="tint">Template + salesperson</Badge></Text>
          </div>
        </Card>

        {/* PDF Preview */}
        <Card className={styles.sectionCard}>
          <CardHeader header={<Text weight="semibold">Document Preview</Text>} />
          <div style={{ padding: tokens.spacingHorizontalM }}>
            {previewUrl || generating ? (
              <>
                {previewUrl ? (
                  <iframe
                    className={styles.pdfFrame}
                    src={previewUrl}
                    title="Proposal Preview"
                  />
                ) : (
                  <div className={styles.pdfFrame} style={{
                    display: "flex", alignItems: "center", justifyContent: "center",
                    backgroundColor: tokens.colorNeutralBackground3,
                  }}>
                    <Spinner size="small" label="Generating and loading PDF…" />
                  </div>
                )}
                <div style={{ marginTop: tokens.spacingVerticalS, display: "flex", gap: tokens.spacingHorizontalS }}>
                  <Button
                    appearance="outline"
                    icon={<ArrowDownload24Regular />}
                    onClick={handleDownloadDraft}
                    size="small"
                  >
                    Download Draft PDF
                  </Button>
                  <Button
                    appearance="outline"
                    icon={<Open24Regular />}
                    onClick={handleDownloadDraft}
                    size="small"
                  >
                    Open in New Tab
                  </Button>
                  <Button
                    appearance="outline"
                    icon={<Eye24Regular />}
                    onClick={handleGeneratePreview}
                    disabled={generating}
                    size="small"
                  >
                    Regenerate Preview
                  </Button>
                </div>
              </>
            ) : (
              <Button
                appearance="primary"
                icon={<Eye24Regular />}
                onClick={handleGeneratePreview}
                disabled={generating}
              >
                {generating ? <Spinner size="tiny" label="Generating…" /> : "Generate Preview PDF"}
              </Button>
            )}
          </div>
        </Card>
      </div>

      <div className={styles.footer}>
        <Button appearance="outline" onClick={onEdit}>
          Edit Fields
        </Button>
        <Button
          appearance="primary"
          icon={<Send24Regular />}
          onClick={handleSubmit}
          disabled={submitting || !proposal.artifacts.draft_pdf_blob}
        >
          {submitting ? <Spinner size="tiny" /> : "Submit for Approval"}
        </Button>
      </div>
    </div>
  );
};
